import React, { ComponentType, ReactElement, useMemo } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import {
  areEqual,
  FixedSizeList,
  FixedSizeListProps,
  VariableSizeList,
  VariableSizeListProps
} from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'

import cx from 'classnames'

import { AccountActivitySortValue } from 'modules/accountActivity/domain'
import mergeRefs from 'utils/mergeRefs'

import styles from './styles.module.css'
interface TableColumn {
  label: string
  margin?: string
  sortable: boolean
  sortValue?: AccountActivitySortValue
  initialDescending?: boolean
  icon?: ReactElement
}

interface InfiniteScrollTableProps {
  loadingComponent: any
  errorComponent: any
  emptyComponent: any
  height?: number
  error?: Error | unknown
  data: any[]
  resultSize: number
  columns: TableColumn[]
  loadMore: () => void
  cellRenderers: any[] //make data and any types fit together
  onRowClick: (a?: any, b?: any) => any | void
  rowHeight: number | ((index: number) => number)
  areRowsEqual?: (row1: any, row2: any) => boolean
  isLoadingInitialData: boolean
  initialDataLoaded: boolean
  rowClassName: any
  selectedSort: AccountActivitySortValue
  onSortByChange: (value?: any) => void
  descending: boolean
  onSortDirChange: (value?: any) => void | any
  initialScrollOffset?: number
  onScrollOffsetChange?: (offset: number) => void
  forwardedRef?: React.MutableRefObject<any>
  listStyle?: React.CSSProperties
}

const handleSortableHeaderClick = (
  sortValue: AccountActivitySortValue | undefined,
  prevSortValue: AccountActivitySortValue,
  prevDescending: boolean,
  initialDescending: boolean,
  onSortByChange: (a: boolean | undefined | AccountActivitySortValue) => void,
  onSortDirChange: (a: boolean) => void
) => {
  if (sortValue === prevSortValue) {
    onSortDirChange(!prevDescending)
  } else {
    onSortByChange(sortValue)
    onSortDirChange(initialDescending)
  }
}

const TableHeader = ({
  columns,
  children,
  rowClassName,
  selectedSort,
  onSortByChange,
  descending,
  onSortDirChange
}: {
  columns: TableColumn[]
  children?: any
  rowClassName: any
  selectedSort: AccountActivitySortValue
  onSortByChange: () => void
  descending: boolean
  onSortDirChange: () => void
}) => {
  return (
    <table className={styles.tableStyle}>
      <thead className={styles.tableHeader}>
        <tr className={rowClassName}>
          {columns.map(
            (
              {
                label,
                margin = '0px',
                sortable,
                sortValue,
                initialDescending = true,
                icon = false
              },
              index
            ) => {
              return (
                <th
                  key={`header-cell-${index}`}
                  className={cx({
                    [styles.sortableColumnHeader]: sortable,
                    [styles.sorted]: sortable && selectedSort === sortValue,
                    [styles.sortAsc]: !descending,
                    [styles.sortDesc]: descending
                  })}
                  style={{ marginLeft: margin }}
                  onClick={() =>
                    sortable &&
                    handleSortableHeaderClick(
                      sortValue,
                      selectedSort,
                      descending,
                      initialDescending,
                      onSortByChange,
                      onSortDirChange
                    )
                  }>
                  <span className={cx({ [styles.labelContainer]: !!icon })}>
                    {label}
                    {icon && icon}
                  </span>
                </th>
              )
            }
          )}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

const TableBody = (props: any) => {
  return <tbody {...props}>{props.children}</tbody>
}

const rowRenderer = (
  equalityFunction: (row1: any, row2: any) => boolean,
  cellRenderers: any[],
  onRowClick: any,
  rowClassName: any
) =>
  React.memo(({ index, style, data }: { index: number; style: any; data: any }) => {
    const dataItem = data[index]
    return (
      <tr
        className={cx(styles.rowContainerStyle, rowClassName)}
        style={style}
        onClick={() => onRowClick(dataItem, index)}>
        {cellRenderers.map((renderer, i) => (
          <td key={i} className={styles.cellStyle}>
            {renderer(dataItem, index)}
          </td>
        ))}
      </tr>
    )
  }, equalityFunction)

export const InfiniteScrollTable = ({
  data,
  columns,
  loadMore,
  error,
  cellRenderers,
  resultSize,
  onRowClick,
  rowHeight,
  height,
  rowClassName,
  areRowsEqual = areEqual,
  initialDataLoaded,
  emptyComponent,
  isLoadingInitialData,
  errorComponent,
  loadingComponent,
  selectedSort,
  onSortByChange,
  descending,
  onSortDirChange,
  initialScrollOffset = 0,
  onScrollOffsetChange = () => {},
  forwardedRef,
  listStyle = {}
}: InfiniteScrollTableProps) => {
  const memoizedRowRenderer = useMemo(
    () => rowRenderer(areRowsEqual, cellRenderers, onRowClick, rowClassName),
    [areRowsEqual, cellRenderers, onRowClick, rowClassName]
  )

  const List: ComponentType<VariableSizeListProps> | ComponentType<FixedSizeListProps> =
    typeof rowHeight === 'function' ? VariableSizeList : FixedSizeList

  return (
    <TableHeader
      columns={columns}
      rowClassName={rowClassName}
      selectedSort={selectedSort}
      onSortByChange={onSortByChange}
      descending={descending}
      onSortDirChange={onSortDirChange}>
      {initialDataLoaded && data.length > 0 && (
        <AutoSizer className={styles.tableViewContainer} disableHeight={!!height}>
          {({ height: autoHeight, width }) => (
            <InfiniteLoader
              isItemLoaded={(index: number) => !!data[index]}
              itemCount={resultSize ?? 0}
              loadMoreItems={() => Promise.resolve(loadMore())}>
              {({ onItemsRendered, ref }) => (
                <List
                  className="table-body-list"
                  style={listStyle}
                  innerElementType={TableBody}
                  itemData={data}
                  overscanCount={0}
                  height={data.length > 1 ? (height || autoHeight) + 112 : 56}
                  itemCount={data.length}
                  itemSize={rowHeight as any}
                  onItemsRendered={onItemsRendered}
                  initialScrollOffset={initialScrollOffset}
                  onScroll={({ scrollOffset }) => onScrollOffsetChange(scrollOffset)}
                  ref={forwardedRef ? mergeRefs([forwardedRef, ref]) : ref}
                  width={width}>
                  {memoizedRowRenderer}
                </List>
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      )}
      {initialDataLoaded && data.length === 0 && (
        <div className={styles.noDataStyle}>{emptyComponent()}</div>
      )}
      {error && <div className={styles.noDataStyle}>{errorComponent()}</div>}
      {isLoadingInitialData && <div className={styles.noDataStyle}>{loadingComponent()}</div>}
    </TableHeader>
  )
}
